import { refreshTokens } from './token';
import { saveSession, getStoredRefreshToken, getSessionExpiry } from './session';

// Refresh when less than 2 minutes remain; check every minute.
const REFRESH_THRESHOLD_MS = 2 * 60 * 1000;
const CHECK_INTERVAL_MS = 60 * 1000;

let intervalId = null;
let refreshInFlight = null;

// The regional Cognito IDP `InitiateAuth` fallback was removed: the corporate proxy
// blocks `cognito-idp.<region>.amazonaws.com`, so it could only fail with a CORS error.
const REFRESH_PATHS = [
  { name: 'Cognito Hosted UI /oauth2/token', run: refreshTokens },
];

/** Refresh the session immediately using the stored refresh_token. */
export const refreshCognitoSession = async () => {
  const refreshToken = getStoredRefreshToken();
  if (!refreshToken) return null;

  // Collapse concurrent refreshes into a single in-flight request.
  if (refreshInFlight) return refreshInFlight;

  refreshInFlight = (async () => {
    let lastError = new Error('No Cognito refresh path is available');

    for (let i = 0; i < REFRESH_PATHS.length; i += 1) {
      const path = REFRESH_PATHS[i];
      try {
        const tokens = await path.run({ refreshToken });
        if (tokens && tokens.access_token && tokens.id_token) {
          saveSession(tokens);
          return tokens;
        }
        lastError = new Error(`${path.name} returned no usable tokens`);
      } catch (error) {
        // Dead refresh_token: the remaining paths would fail identically.
        if (error && error.fatal) throw error;
        lastError = error;
      }
      // eslint-disable-next-line no-console
      console.warn(`Cognito refresh via ${path.name} failed:`, lastError && lastError.message);
    }

    throw lastError;
  })().finally(() => {
    refreshInFlight = null;
  });

  return refreshInFlight;
};

/** Refresh only when the access_token is close to expiring. */
export const ensureFreshAccessToken = async () => {
  if (!getStoredRefreshToken()) return false;

  const expiresAt = getSessionExpiry();
  if (expiresAt && expiresAt - Date.now() > REFRESH_THRESHOLD_MS) {
    return false;
  }

  await refreshCognitoSession();
  return true;
};

/** Stop the background token-refresh scheduler. */
export const stopTokenRefreshScheduler = () => {
  if (intervalId !== null) {
    clearInterval(intervalId);
    intervalId = null;
  }
};

/** Start the background scheduler; no-op when no refresh_token is available. */
export const startTokenRefreshScheduler = () => {
  if (intervalId !== null) return;
  if (!getStoredRefreshToken()) return;

  intervalId = setInterval(() => {
    ensureFreshAccessToken().catch((error) => {
      // Only an expired/revoked refresh_token is unrecoverable; stop and let the next
      // API call surface the 401 for re-login. Transient failures (network blip, sleep/
      // wake, DNS hiccup) must keep the scheduler alive so the next tick can retry.
      if (error && error.fatal) {
        stopTokenRefreshScheduler();
      }
    });
  }, CHECK_INTERVAL_MS);
};
